/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React from 'react';
import { cn } from '@/shared/ui/utils/tailwind';
import { VISIT_ACCENT_COLORS } from '../constants/visit-colors';
import type { VisitStatus } from '../constants/visit-colors';
import { ListItemCard, Text, UnstyledButton } from '@/shared/ui';
import { ChevronDownIcon } from '@/shared/ui/radix-icons';
import { useVisitStatusLabels } from '../constants/visit-colors';
import { useTranslation } from 'react-i18next';

interface KanbanCardProps {
  id: string;
  property: string;
  client: string;
  date: string;
  time: string;
  broker: string;
  status: VisitStatus;
  selected: boolean;
  onClick: () => void;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  /**
   * Turns the status into a button that opens the move sheet.
   *
   * Only the phone passes it. On desktop the status changes by dragging the
   * card between columns, and a button inside a draggable card would swallow
   * the press that was meant to start the drag.
   */
  onMoveStatus?: () => void;
}

export const KanbanCard: React.FC<KanbanCardProps> = ({
  property, client, date, time, broker, status, selected, onClick, onDragStart, onDragEnd,
  onMoveStatus,
}) => {
  const { t } = useTranslation('features/visits');
  const statusLabels = useVisitStatusLabels();
  const accent = VISIT_ACCENT_COLORS[status];
  const formattedDate = date.split('-').reverse().slice(0, 2).join('/');

  const [clientFirst, ...clientRest] = client.split(' ');
  const clientLast = clientRest.join(' ');

  return (
    <div
      data-testid="kanban-card"
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={cn(
        'cursor-grab select-none',
        selected && 'ring-1 ring-[var(--seed-base)] rounded-(--radius-lg)',
      )}
    >
      <ListItemCard
        accentColor={accent}
        firstName={clientFirst}
        lastName={clientLast || undefined}
        badges={[{ label: `${formattedDate} ${time}`, variant: 'accent' }]}
        description={property}
        metadata={broker ? [broker] : []}
        onClick={onClick}
        footer={onMoveStatus ? (
          /*
           * The status pill is the control, and it sits outside the card's
           * own click target: the card opens the visit and this moves it.
           * 32px so a thumb can hit it without hitting the card underneath.
           */
          <UnstyledButton
            data-testid="visit-card-status"
            aria-label={t('move.open', { status: statusLabels[status] })}
            onClick={(event) => {
              event.stopPropagation();
              onMoveStatus();
            }}
            className="flex h-8 items-center gap-(--space-1) self-start rounded-full px-(--space-3)"
            style={{
              background: `color-mix(in srgb, ${accent} 12%, var(--surface-primary))`,
              color: accent,
              fontSize: 'var(--text-eyebrow)',
              fontWeight: 'var(--font-semibold)' as unknown as number,
              letterSpacing: 'var(--tracking-chip)',
            }}
          >
            {statusLabels[status]}
            <ChevronDownIcon width={12} height={12} aria-hidden="true" />
          </UnstyledButton>
        ) : undefined}
      />
    </div>
  );
};
